import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
const API_HOST =
  process.env.REACT_APP_API_URL ||
  "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net";

const API_BASE = API_HOST + "/api/listing-agent";

// ═════════════════════════════════════════════════════════════════════════════
// DESIGN TOKENS
// Calmer, mostly-grayscale dashboard. One quiet accent per marketplace used
// only for links/selection state; red is reserved for things that actually
// need attention (critical issues), amber for warnings.
// ═════════════════════════════════════════════════════════════════════════════
const MARKETPLACE_CFG = {
  ebay: { label: "eBay", accent: "#3f3f46", accentSoft: "#f4f4f5" },
  vinted: { label: "Vinted", accent: "#3f3f46", accentSoft: "#f4f4f5" },
};

const PRIORITY_CFG = {
  high: { label: "High", color: "#b91c1c" },
  medium: { label: "Medium", color: "#92400e" },
  low: { label: "Low", color: "#71717a" },
};

const SEVERITY_CFG = {
  critical: { color: "#b91c1c", tone: "danger" },
  warning: { color: "#92400e", tone: "warning" },
  info: { color: "#52525b", tone: "neutral" },
};

const AREA_LABEL = {
  title: "Title",
  price: "Price",
  image_or_price: "Photo / Price",
  description_or_price: "Description / Price",
  seo: "Search visibility",
  visibility: "Visibility",
  photos_or_price: "Photos / Price",
  staleness: "Freshness",
};

const GLOBAL_STYLES = `
  .la-root { background:#fafafa; min-height:100vh; color:#18181b;
    font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Inter,sans-serif;
    -webkit-font-smoothing:antialiased; }
  .la-shell { max-width:1240px; margin:0 auto; padding:36px 24px 64px; }

  .la-eyebrow { font-size:11px; font-weight:700; color:#a1a1aa; text-transform:uppercase; letter-spacing:.06em; margin:0 0 6px; }
  .la-title { font-size:22px; font-weight:700; letter-spacing:-0.01em; margin:0 0 4px; color:#18181b; }
  .la-subtitle { font-size:13px; color:#71717a; margin:0; line-height:1.5; }

  .la-segment { display:inline-flex; background:#f0f0f1; border-radius:10px; padding:3px; gap:2px; }
  .la-segment button { border:none; background:transparent; padding:7px 16px; font-size:13px; font-weight:600;
    border-radius:8px; cursor:pointer; color:#71717a; transition:background .12s,color .12s; }
  .la-segment button.active { background:#fff; color:#18181b; box-shadow:0 1px 2px rgba(0,0,0,.08); }

  .la-btn { border:none; border-radius:8px; padding:9px 16px; font-size:13px; font-weight:600; cursor:pointer;
    display:inline-flex; align-items:center; gap:7px; white-space:nowrap; transition:opacity .12s,background .12s; }
  .la-btn-primary { background:#18181b; color:#fff; }
  .la-btn-primary:disabled { background:#e4e4e7; color:#a1a1aa; cursor:not-allowed; }
  .la-btn-primary:not(:disabled):hover { opacity:.88; }
  .la-btn-ghost { background:#fff; color:#3f3f46; border:1px solid #e4e4e7; }
  .la-btn-ghost:hover { background:#f7f7f7; }
  .la-btn-ghost:disabled { color:#a1a1aa; cursor:not-allowed; }
  .la-btn-sm { padding:6px 12px; font-size:12px; border-radius:7px; }
  .la-btn-icon { width:32px; height:32px; padding:0; justify-content:center; }

  .la-card { background:#fff; border:1px solid #e4e4e7; border-radius:14px; }
  .la-card + .la-card { margin-top:16px; }
  .la-card-head { padding:16px 20px; border-bottom:1px solid #f0f0f1; display:flex; align-items:center;
    justify-content:space-between; gap:12px; flex-wrap:wrap; }
  .la-card-title { font-size:13px; font-weight:700; color:#18181b; }
  .la-card-body { padding:20px; }

  .la-stats { display:grid; grid-template-columns:repeat(auto-fit,minmax(170px,1fr)); gap:12px; margin-bottom:20px; }
  .la-stat { background:#fff; border:1px solid #e4e4e7; border-radius:14px; padding:18px 20px; }
  .la-stat-label { font-size:11px; font-weight:700; color:#a1a1aa; text-transform:uppercase; letter-spacing:.05em; margin-bottom:10px; }
  .la-stat-value { font-size:28px; font-weight:700; letter-spacing:-0.02em; line-height:1; }
  .la-stat-sub { font-size:11.5px; color:#a1a1aa; margin-top:6px; }

  .la-search { display:flex; align-items:center; gap:8px; background:#fff; border:1px solid #e4e4e7;
    border-radius:9px; padding:8px 12px; min-width:220px; flex:1; max-width:360px; }
  .la-search input { border:none; outline:none; font-size:13px; width:100%; background:transparent; color:#18181b; }
  .la-search input::placeholder { color:#a1a1aa; }
  .la-search svg { flex-shrink:0; color:#a1a1aa; }

  .la-toggle-row { display:flex; align-items:center; gap:8px; font-size:12.5px; color:#3f3f46; user-select:none; cursor:pointer; }
  .la-toggle-row input { accent-color:#18181b; width:14px; height:14px; cursor:pointer; }

  .la-table-wrap { overflow-x:auto; }
  .la-table { width:100%; border-collapse:collapse; font-size:13px; }
  .la-table th { text-align:left; font-size:10.5px; font-weight:700; color:#a1a1aa; text-transform:uppercase;
    letter-spacing:.04em; padding:11px 20px; background:#fafafa; border-bottom:1px solid #e4e4e7; white-space:nowrap; }
  .la-table td { padding:14px 20px; border-bottom:1px solid #f0f0f1; vertical-align:middle; }
  .la-table tbody tr:last-child td { border-bottom:none; }
  .la-table tbody tr:hover td { background:#fafafa; }
  .la-row-title { font-weight:600; color:#18181b; line-height:1.4; display:block; margin-bottom:2px; text-decoration:none; }
  .la-row-title.linked:hover { text-decoration:underline; }
  .la-row-id { font-size:10.5px; color:#c4c4c8; font-family:ui-monospace,monospace; }
  .la-row-sku { font-size:10.5px; color:#71717a; font-family:ui-monospace,monospace; }

  .la-tag { display:inline-flex; align-items:center; font-size:11px; font-weight:700; padding:3px 9px; border-radius:6px; line-height:1.5; }
  .la-tag-danger { background:#fef2f2; color:#b91c1c; }
  .la-tag-warning { background:#fffbeb; color:#92400e; }
  .la-tag-neutral { background:#f4f4f5; color:#52525b; }
  .la-tag-success { background:#f4f4f5; color:#3f7a4e; }

  .la-empty { padding:64px 24px; text-align:center; }
  .la-empty-title { font-size:15px; font-weight:700; color:#18181b; margin-bottom:6px; }
  .la-empty-sub { font-size:13px; color:#a1a1aa; }

  .la-action { border:1px solid #e4e4e7; border-left:3px solid; border-radius:10px; padding:14px 16px; background:#fff; }
  .la-action-top { display:flex; align-items:center; gap:8px; margin-bottom:8px; }
  .la-action-priority { font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; }
  .la-action-area { font-size:10.5px; font-weight:600; color:#a1a1aa; text-transform:uppercase; letter-spacing:.04em; }
  .la-action-issue { font-size:13px; font-weight:600; color:#18181b; margin-bottom:6px; line-height:1.45; }
  .la-action-fix { font-size:12.5px; color:#52525b; line-height:1.55; }

  .la-issue-pop { display:flex; flex-direction:column; gap:8px; }
  .la-issue-pop-item { border-left:3px solid; padding:6px 0 6px 10px; }
  .la-issue-pop-label { font-size:11.5px; font-weight:700; margin-bottom:2px; }
  .la-issue-pop-detail { font-size:11.5px; color:#71717a; line-height:1.5; }

  .la-list-row { display:flex; gap:14px; align-items:flex-start; background:#fafafa; border:1px solid #f0f0f1;
    border-radius:10px; padding:12px 14px; text-decoration:none; transition:border-color .12s,background .12s; }
  .la-list-row.linked:hover { border-color:#d4d4d8; background:#fff; }
  .la-rank { width:22px; height:22px; border-radius:50%; flex-shrink:0; display:flex; align-items:center;
    justify-content:center; font-size:10px; font-weight:700; }
  .la-list-title { font-size:12.5px; font-weight:600; margin-bottom:5px; line-height:1.4; overflow:hidden;
    text-overflow:ellipsis; white-space:nowrap; }
  .la-list-meta { display:flex; gap:10px; flex-wrap:wrap; align-items:center; font-size:11.5px; color:#71717a; }

  .la-toast { position:fixed; top:20px; right:20px; z-index:9999; border-radius:10px; padding:11px 16px; font-weight:600;
    font-size:13px; box-shadow:0 8px 24px rgba(0,0,0,.12); display:flex; align-items:center; gap:8px; }
  .la-drawer-overlay, .la-modal-overlay { position:fixed; inset:0; background:rgba(24,24,27,.32); z-index:999; }
  .la-drawer { position:fixed; top:0; right:0; bottom:0; width:min(460px,100vw); background:#fff;
    box-shadow:-12px 0 40px rgba(0,0,0,.12); z-index:1000; display:flex; flex-direction:column; }
  .la-modal { position:fixed; top:50%; left:50%; transform:translate(-50%,-50%); width:min(480px,92vw);
    max-height:86vh; overflow-y:auto; background:#fff; border-radius:16px; box-shadow:0 20px 60px rgba(0,0,0,.2);
    z-index:1001; }

  .la-field { display:flex; flex-direction:column; gap:5px; margin-bottom:14px; }
  .la-field label { font-size:12px; font-weight:600; color:#3f3f46; }
  .la-field-hint { font-size:11px; color:#a1a1aa; }
  .la-field input[type="number"] { border:1px solid #e4e4e7; border-radius:8px; padding:8px 10px; font-size:13px;
    width:120px; outline:none; }
  .la-field input[type="number"]:focus { border-color:#a1a1aa; }
  .la-field-check { display:flex; align-items:center; gap:8px; }
  .la-field-check input { width:15px; height:15px; accent-color:#18181b; cursor:pointer; }

  .la-progress-track { background:#f0f0f1; border-radius:99px; height:6px; overflow:hidden; }
  .la-progress-fill { height:100%; border-radius:99px; transition: width .3s ease; }

  .la-skel { border-radius:6px; background:linear-gradient(90deg,#f1f1f2 25%,#e8e8ea 50%,#f1f1f2 75%);
    background-size:200% 100%; animation:la-shimmer 1.3s infinite; }
  .la-spin { display:inline-block; border-radius:50%; border:2px solid rgba(255,255,255,.4); border-top-color:#fff;
    animation:la-spin .7s linear infinite; }

  @keyframes la-shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
  @keyframes la-spin { to{transform:rotate(360deg)} }
  @keyframes la-fade-up { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
  @keyframes la-slide-in { from{transform:translateX(16px);opacity:0} to{transform:translateX(0);opacity:1} }
`;

// ─────────────────────────────────────────────────────────────────────────────
// FORMATTERS
// ─────────────────────────────────────────────────────────────────────────────
const fmtPrice = (n, cur = "GBP") => {
  if (n == null || isNaN(n)) return "—";
  const sym = cur === "EUR" ? "€" : cur === "USD" ? "$" : "£";
  return `${sym}${Number(n).toFixed(2)}`;
};
const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString("en-GB"));
const pct = (n) => (n == null ? "—" : `${n}%`);
const truncate = (s, n) => (s?.length > n ? s.slice(0, n) + "…" : s || "—");

function useDebounced(value, delay = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ─────────────────────────────────────────────────────────────────────────────
// PRIMITIVES
// ─────────────────────────────────────────────────────────────────────────────
const Sk = ({ w = "100%", h = 14, style = {} }) => (
  <div className="la-skel" style={{ width: w, height: h, ...style }} />
);

const Card = ({ title, action, children }) => (
  <div className="la-card">
    {title && (
      <div className="la-card-head">
        <span className="la-card-title">{title}</span>
        {action}
      </div>
    )}
    <div className="la-card-body">{children}</div>
  </div>
);

const Tag = ({ tone = "neutral", children }) => (
  <span className={`la-tag la-tag-${tone}`}>{children}</span>
);

// Issue badge: a compact tag that reveals the full, worded explanation for
// every flagged issue in a small popover.
//
// Rendered via a React Portal straight into document.body — NOT as a normal
// child in the table. Reason: this app shell (or something in the ancestor
// tree — a page-transition wrapper, a scaled container, anything with
// `transform`/`filter`/`will-change`) creates a new containing block for
// `position: fixed` descendants. Per the CSS spec, once ANY ancestor has a
// transform, `position: fixed` inside it stops being relative to the
// viewport and becomes relative to that transformed ancestor instead — even
// though we compute coordinates with getBoundingClientRect() (viewport-
// relative). That mismatch is exactly what caused "sometimes correct,
// sometimes overlapping/in the wrong spot": it depended on scroll position
// and layout state of whatever transformed ancestor was involved. A portal
// to document.body sidesteps this entirely, since body is never inside a
// transformed context.
//
// Closes on: explicit outside click, window scroll (any ancestor, capture
// phase), or resize — so it never shows a stale/wrong position instead of
// trying to reposition itself, which would still race against the actual
// layout.
const IssueBadge = ({ issues }) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const btnRef = useRef(null);
  const popRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const onDocClick = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (popRef.current?.contains(e.target)) return;
      close();
    };
    document.addEventListener("mousedown", onDocClick);
    window.addEventListener("scroll", close, true); // capture: any scrollable ancestor
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  if (!issues || issues.length === 0)
    return <Tag tone="success">No issues</Tag>;
  const worstTone = issues.some((i) => i.severity === "critical")
    ? "danger"
    : issues.some((i) => i.severity === "warning")
      ? "warning"
      : "neutral";

  const POP_WIDTH = 300;

  const toggle = () => {
    if (open) {
      setOpen(false);
      return;
    }
    const rect = btnRef.current?.getBoundingClientRect();
    if (!rect) return;
    const left = Math.min(
      Math.max(rect.right - POP_WIDTH, 8),
      window.innerWidth - POP_WIDTH - 8,
    );
    setCoords({ top: rect.bottom + 6, left });
    setOpen(true);
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={`la-tag la-tag-${worstTone}`}
        style={{ border: "none", cursor: "pointer" }}
        onClick={toggle}
      >
        {issues.length} issue{issues.length === 1 ? "" : "s"}
      </button>
      {open &&
        coords &&
        createPortal(
          <div
            ref={popRef}
            style={{
              position: "fixed",
              top: coords.top,
              left: coords.left,
              width: POP_WIDTH,
              background: "#fff",
              border: "1px solid #e4e4e7",
              borderRadius: 10,
              boxShadow: "0 8px 24px rgba(0,0,0,.16)",
              padding: 12,
              zIndex: 2000,
              textAlign: "left",
            }}
          >
            <div className="la-issue-pop">
              {issues.map((iss, i) => {
                const cfg = SEVERITY_CFG[iss.severity] || SEVERITY_CFG.info;
                return (
                  <div
                    key={i}
                    className="la-issue-pop-item"
                    style={{ borderLeftColor: cfg.color }}
                  >
                    <div
                      className="la-issue-pop-label"
                      style={{ color: cfg.color }}
                    >
                      {iss.label}
                    </div>
                    <div className="la-issue-pop-detail">{iss.detail}</div>
                  </div>
                );
              })}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

const RankBadge = ({ i, accent }) => (
  <div
    className="la-rank"
    style={{
      background: i < 3 ? accent : "#e4e4e7",
      color: i < 3 ? "#fff" : "#71717a",
    }}
  >
    {i + 1}
  </div>
);

const DiffTag = ({ diff }) => {
  if (diff == null) return null;
  const tone = diff > 0 ? "danger" : diff < 0 ? "success" : "neutral";
  const text =
    diff > 0
      ? `£${diff} higher`
      : diff < 0
        ? `£${Math.abs(diff)} lower`
        : "same price";
  return <Tag tone={tone}>{text}</Tag>;
};

const EmptyState = ({ title, sub }) => (
  <div className="la-empty">
    <div className="la-empty-title">{title}</div>
    <div className="la-empty-sub">{sub}</div>
  </div>
);

const ActionItem = ({ action, index }) => {
  const cfg = PRIORITY_CFG[action.priority] || PRIORITY_CFG.low;
  return (
    <div
      className="la-action"
      style={{
        borderLeftColor: cfg.color,
        animation: `la-fade-up .25s ease ${index * 0.05}s both`,
      }}
    >
      <div className="la-action-top">
        <span className="la-action-priority" style={{ color: cfg.color }}>
          {cfg.label}
        </span>
        <span style={{ color: "#e4e4e7" }}>·</span>
        <span className="la-action-area">
          {AREA_LABEL[action.area] || action.area}
        </span>
      </div>
      <div className="la-action-issue">{action.issue}</div>
      <div className="la-action-fix">{action.fix}</div>
    </div>
  );
};

const ActionList = ({ actions }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
    {actions.map((a, i) => (
      <ActionItem key={i} action={a} index={i} />
    ))}
  </div>
);

const StatRow = ({ label, value, strong }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      padding: "9px 0",
      borderBottom: "1px solid #f0f0f1",
    }}
  >
    <span
      style={{
        fontSize: 12.5,
        color: "#71717a",
        fontWeight: strong ? 600 : 400,
      }}
    >
      {label}
    </span>
    <span
      style={{
        fontSize: strong ? 16 : 13.5,
        fontWeight: 700,
        color: strong ? "#18181b" : "#3f3f46",
      }}
    >
      {value}
    </span>
  </div>
);

const PriceStatusNote = ({
  status,
  diff,
  referenceLabel = "reference average",
}) => {
  if (diff == null) return null;
  const tone =
    status === "too_high"
      ? "danger"
      : status === "too_low"
        ? "success"
        : "neutral";
  const text =
    diff > 0
      ? `£${diff.toFixed(2)} above the ${referenceLabel}`
      : diff < 0
        ? `£${Math.abs(diff).toFixed(2)} below the ${referenceLabel}`
        : `Matches the ${referenceLabel}`;
  return (
    <div style={{ marginTop: 12 }}>
      <Tag tone={tone}>{text}</Tag>
    </div>
  );
};

const PriceBandChart = ({ bands, ourPrice, accent }) => {
  const labels = {
    under10: "Under £10",
    ten_20: "£10–20",
    twenty_30: "£20–30",
    over30: "Over £30",
  };
  const max = Math.max(...Object.values(bands), 1);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {Object.entries(bands).map(([key, count]) => {
        const inRange =
          (key === "under10" && ourPrice < 10) ||
          (key === "ten_20" && ourPrice >= 10 && ourPrice < 20) ||
          (key === "twenty_30" && ourPrice >= 20 && ourPrice < 30) ||
          (key === "over30" && ourPrice >= 30);
        return (
          <div
            key={key}
            style={{ display: "flex", alignItems: "center", gap: 10 }}
          >
            <div
              style={{
                width: 68,
                fontSize: 11,
                color: inRange ? accent : "#a1a1aa",
                fontWeight: inRange ? 700 : 500,
                flexShrink: 0,
              }}
            >
              {labels[key]}
            </div>
            <div
              style={{
                flex: 1,
                background: "#f0f0f1",
                borderRadius: 99,
                height: 7,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: "100%",
                  borderRadius: 99,
                  width: `${(count / max) * 100}%`,
                  background: inRange ? accent : "#d4d4d8",
                }}
              />
            </div>
            <div
              style={{
                width: 18,
                fontSize: 11,
                fontWeight: 700,
                color: "#71717a",
                textAlign: "right",
              }}
            >
              {count}
            </div>
          </div>
        );
      })}
      {ourPrice != null && (
        <div style={{ fontSize: 11, color: "#a1a1aa", marginTop: 2 }}>
          Your price: {fmtPrice(ourPrice)}
        </div>
      )}
    </div>
  );
};

const PriceRangeBar = ({ min, max, avg, our, accent }) => {
  if (min == null || max == null || our == null || max <= min) {
    return (
      <div style={{ fontSize: 12, color: "#a1a1aa" }}>
        Not enough similar listings yet to plot a range.
      </div>
    );
  }
  const clamp = (v) =>
    Math.min(100, Math.max(0, ((v - min) / (max - min)) * 100));
  return (
    <div>
      <div
        style={{
          position: "relative",
          height: 6,
          background: "#f0f0f1",
          borderRadius: 99,
          margin: "20px 8px 10px",
        }}
      >
        {avg != null && (
          <div
            style={{
              position: "absolute",
              left: `${clamp(avg)}%`,
              top: -6,
              width: 2,
              height: 18,
              background: "#d4d4d8",
            }}
          />
        )}
        <div
          style={{
            position: "absolute",
            left: `${clamp(our)}%`,
            top: -5,
            width: 12,
            height: 16,
            borderRadius: 4,
            background: accent,
            border: "2px solid #fff",
            boxShadow: "0 1px 3px rgba(0,0,0,.25)",
            transform: "translateX(-6px)",
          }}
        />
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: 10.5,
          color: "#a1a1aa",
          fontWeight: 600,
          padding: "0 8px",
        }}
      >
        <span>{fmtPrice(min)} lowest</span>
        <span>{fmtPrice(max)} highest</span>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// RULES MODAL — edit the persisted thresholds that define "low performing"
// ─────────────────────────────────────────────────────────────────────────────
const RulesModal = ({ marketplace, scope, onClose, onSaved }) => {
  const [rules, setRules] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const isEbay = marketplace === "ebay";

  useEffect(() => {
    setLoading(true);
    const params = isEbay
      ? { marketplace, company_id: scope.company_id }
      : { marketplace, profile_username: scope.profile_username };
    axios
      .get(`${API_BASE}/rules`, { params })
      .then((r) => setRules(r.data.rules))
      .catch((e) => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [marketplace, scope.company_id, scope.profile_username, isEbay]);

  const update = (key, value) => setRules((r) => ({ ...r, [key]: value }));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const body = isEbay
        ? { marketplace, company_id: scope.company_id, rules }
        : { marketplace, profile_username: scope.profile_username, rules };
      const r = await axios.put(`${API_BASE}/rules`, body);
      onSaved(r.data.rules);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="la-modal-overlay" onClick={onClose} />
      <div className="la-modal">
        <div
          style={{
            padding: "18px 22px",
            borderBottom: "1px solid #f0f0f1",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ fontSize: 14.5, fontWeight: 700 }}>
            Low-performing rules — {MARKETPLACE_CFG[marketplace].label}
          </div>
          <button className="la-btn la-btn-ghost la-btn-icon" onClick={onClose}>
            ✕
          </button>
        </div>
        <div style={{ padding: 22 }}>
          {loading && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <Sk w="100%" h={40} />
              <Sk w="100%" h={40} />
              <Sk w="100%" h={40} />
            </div>
          )}
          {error && (
            <div
              style={{
                background: "#fef2f2",
                borderRadius: 8,
                padding: 10,
                color: "#991b1b",
                fontSize: 12.5,
                marginBottom: 14,
              }}
            >
              {error}
            </div>
          )}
          {rules && !loading && isEbay && (
            <>
              <div className="la-field">
                <label>Flag if impressions below</label>
                <input
                  type="number"
                  min="0"
                  value={rules.impressions_below}
                  onChange={(e) =>
                    update("impressions_below", Number(e.target.value))
                  }
                />
                <span className="la-field-hint">
                  Listings shown fewer than this many times in eBay search get
                  flagged.
                </span>
              </div>
              <div className="la-field">
                <label>Flag if click-through rate below (%)</label>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={rules.ctr_below}
                  onChange={(e) => update("ctr_below", Number(e.target.value))}
                />
                <span className="la-field-hint">
                  CTR = views ÷ impressions × 100.
                </span>
              </div>
              <div className="la-field la-field-check">
                <input
                  type="checkbox"
                  checked={!!rules.zero_views_flag}
                  onChange={(e) => update("zero_views_flag", e.target.checked)}
                />
                <label style={{ margin: 0 }}>
                  Flag listings with impressions but zero views
                </label>
              </div>
              <div className="la-field la-field-check">
                <input
                  type="checkbox"
                  checked={!!rules.zero_sales_flag}
                  onChange={(e) => update("zero_sales_flag", e.target.checked)}
                />
                <label style={{ margin: 0 }}>
                  Flag listings with zero sales
                </label>
              </div>
            </>
          )}
          {rules && !loading && !isEbay && (
            <>
              <div className="la-field">
                <label>Flag if views below</label>
                <input
                  type="number"
                  min="0"
                  value={rules.views_below}
                  onChange={(e) =>
                    update("views_below", Number(e.target.value))
                  }
                />
              </div>
              <div className="la-field">
                <label>Flag if favourites below</label>
                <input
                  type="number"
                  min="0"
                  value={rules.favourites_below}
                  onChange={(e) =>
                    update("favourites_below", Number(e.target.value))
                  }
                />
                <span className="la-field-hint">
                  Set to 1 to flag listings with zero favourites.
                </span>
              </div>
              <div className="la-field">
                <label>Flag as stale after (days)</label>
                <input
                  type="number"
                  min="0"
                  value={rules.stale_days}
                  onChange={(e) => update("stale_days", Number(e.target.value))}
                />
              </div>
            </>
          )}
        </div>
        <div
          style={{
            padding: "16px 22px",
            borderTop: "1px solid #f0f0f1",
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
          }}
        >
          <button className="la-btn la-btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            className="la-btn la-btn-primary"
            disabled={saving || loading}
            onClick={save}
          >
            {saving ? (
              <>
                <span className="la-spin" style={{ width: 13, height: 13 }} />{" "}
                Saving…
              </>
            ) : (
              "Save rules"
            )}
          </button>
        </div>
      </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// DETAIL PAGE
// ─────────────────────────────────────────────────────────────────────────────
const ListingDetailPage = ({ marketplace, itemId, onBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isEbay = marketplace === "ebay";
  const accent = MARKETPLACE_CFG[marketplace].accent;

  useEffect(() => {
    setLoading(true);
    setError(null);
    setData(null);
    const url = isEbay
      ? `${API_BASE}/analysis/${itemId}`
      : `${API_BASE}/vinted/analysis/${itemId}`;
    axios
      .get(url)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.message || e.message))
      .finally(() => setLoading(false));
  }, [itemId, isEbay]);

  return (
    <div className="la-root">
      <style>{GLOBAL_STYLES}</style>

      <div
        style={{
          background: "#fff",
          borderBottom: "1px solid #e4e4e7",
          padding: "14px 24px",
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <div
          style={{
            maxWidth: 900,
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            gap: 14,
          }}
        >
          <button className="la-btn la-btn-ghost la-btn-sm" onClick={onBack}>
            ← Back
          </button>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>
              {MARKETPLACE_CFG[marketplace].label} listing analysis
            </div>
            <div className="la-row-id">{itemId}</div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 900, margin: "0 auto", padding: "28px 24px" }}>
        {loading && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {[...Array(4)].map((_, i) => (
              <div key={i} className="la-card">
                <div className="la-card-body">
                  <Sk w="30%" h={13} style={{ marginBottom: 14 }} />
                  <Sk w="100%" h={44} style={{ marginBottom: 8 }} />
                  <Sk w="70%" h={44} />
                </div>
              </div>
            ))}
          </div>
        )}

        {error && (
          <div
            style={{
              background: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: 12,
              padding: 18,
              color: "#991b1b",
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        {data && !loading && (
          <>
            <Card>
              {isEbay ? (
                <>
                  <div className="la-eyebrow">
                    {data.our_listing?.date_range || "Latest period"}
                  </div>
                  <a
                    href={data.our_listing?.item_url}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      fontSize: 17,
                      fontWeight: 700,
                      color: "#18181b",
                      textDecoration: "none",
                      display: "block",
                      marginBottom: 16,
                      lineHeight: 1.4,
                    }}
                  >
                    {data.our_listing?.title || "—"}
                  </a>
                  <div style={{ display: "flex", gap: 28, flexWrap: "wrap" }}>
                    <HeaderStat
                      label="Price"
                      value={fmtPrice(data.our_listing?.price)}
                      big
                    />
                    <HeaderStat
                      label="Condition"
                      value={data.our_listing?.ebay_condition || "—"}
                    />
                    <HeaderStat
                      label="SKU"
                      value={data.our_listing?.sku || "—"}
                      mono
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="la-eyebrow">
                    {data.our_listing?.brand || "Vinted listing"}
                  </div>
                  <div
                    style={{
                      fontSize: 17,
                      fontWeight: 700,
                      marginBottom: 16,
                      lineHeight: 1.4,
                    }}
                  >
                    {data.our_listing?.title || "—"}
                  </div>
                  <div style={{ display: "flex", gap: 28, flexWrap: "wrap" }}>
                    <HeaderStat
                      label="Price"
                      value={fmtPrice(data.our_listing?.price_parsed)}
                      big
                    />
                    <HeaderStat
                      label="Condition"
                      value={data.our_listing?.condition_status || "—"}
                    />
                    <HeaderStat
                      label="Size"
                      value={data.our_listing?.size || "—"}
                    />
                    <HeaderStat
                      label="Status"
                      value={data.our_listing?.status || "—"}
                    />
                  </div>
                </>
              )}
            </Card>

            {data.actions?.length > 0 && (
              <Card
                title={`${data.actions.length} issue${data.actions.length === 1 ? "" : "s"} to fix`}
              >
                <ActionList actions={data.actions} />
              </Card>
            )}

            {isEbay ? (
              <Card title="Performance">
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3,1fr)",
                    gap: 10,
                    marginBottom: 14,
                  }}
                >
                  <FunnelStep
                    label="Impressions"
                    value={fmt(data.our_listing.impressions)}
                    sub="shown in search"
                    accent="#71717a"
                  />
                  <FunnelStep
                    label="Views"
                    value={fmt(data.our_listing.views)}
                    sub={`${pct(data.our_listing.view_rate)} click-through`}
                    accent={accent}
                  />
                  <FunnelStep
                    label="Sales"
                    value={fmt(data.our_listing.sold)}
                    sub={`${pct(data.our_listing.sell_rate)} conversion`}
                    accent="#3f7a4e"
                  />
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3,1fr)",
                    gap: 10,
                  }}
                >
                  <MiniStat
                    label="External views"
                    value={fmt(data.our_listing?.externalViews)}
                  />
                  <MiniStat
                    label="Available"
                    value={fmt(data.our_listing?.available_items)}
                  />
                  <MiniStat
                    label="CTR"
                    value={pct(data.our_listing?.ctr)}
                    danger={
                      data.our_listing?.ctr < (data.rules?.ctr_below ?? 2)
                    }
                  />
                </div>
              </Card>
            ) : (
              <Card title="Engagement">
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4,1fr)",
                    gap: 10,
                  }}
                >
                  <MiniStat
                    label="Views"
                    value={fmt(data.our_listing?.views)}
                    danger={
                      data.our_listing?.views < (data.rules?.views_below ?? 10)
                    }
                  />
                  <MiniStat
                    label="Favourites"
                    value={fmt(data.our_listing?.favourites)}
                    danger={!data.our_listing?.favourites}
                  />
                  <MiniStat
                    label="Views / day"
                    value={data.our_listing?.views_per_day ?? "—"}
                  />
                  <MiniStat
                    label="Days listed"
                    value={data.our_listing?.days_since_upload ?? "—"}
                    warn={
                      data.our_listing?.days_since_upload >
                      (data.rules?.stale_days ?? 30)
                    }
                  />
                </div>
              </Card>
            )}

            {isEbay ? (
              <Card title="Price">
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 24,
                  }}
                >
                  <div>
                    <StatRow
                      label="Your price"
                      value={fmtPrice(data.price_analysis.our_price)}
                      strong
                    />
                    <StatRow
                      label="Competitor average"
                      value={fmtPrice(data.price_analysis.avg_price)}
                    />
                    <StatRow
                      label="Median"
                      value={fmtPrice(data.price_analysis.median_price)}
                    />
                    <StatRow
                      label="Lowest"
                      value={fmtPrice(data.price_analysis.min_price)}
                    />
                    <StatRow
                      label="Highest"
                      value={fmtPrice(data.price_analysis.max_price)}
                    />
                    <PriceStatusNote
                      status={data.price_analysis.price_status}
                      diff={data.price_analysis.price_diff}
                      referenceLabel="competitor average"
                    />
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: "#a1a1aa",
                        textTransform: "uppercase",
                        letterSpacing: ".04em",
                        marginBottom: 12,
                      }}
                    >
                      Where competitors are priced
                    </div>
                    <PriceBandChart
                      bands={data.price_analysis.price_bands}
                      ourPrice={data.price_analysis.our_price}
                      accent={accent}
                    />
                  </div>
                </div>
              </Card>
            ) : (
              <Card title="Price — compared to your own catalog">
                <div
                  style={{
                    fontSize: 12,
                    color: "#71717a",
                    background: "#fafafa",
                    borderRadius: 8,
                    padding: "10px 12px",
                    marginBottom: 16,
                  }}
                >
                  Vinted has no public competitor search, so this compares your
                  price against your own active listings with the same brand and
                  condition ({data.self_benchmark?.peer_count ?? 0} found).
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 24,
                  }}
                >
                  <div>
                    <StatRow
                      label="Your price"
                      value={fmtPrice(data.our_listing?.price_parsed)}
                      strong
                    />
                    <StatRow
                      label="Your average (similar items)"
                      value={fmtPrice(data.self_benchmark?.avg_price)}
                    />
                    <StatRow
                      label="Lowest"
                      value={fmtPrice(data.self_benchmark?.min_price)}
                    />
                    <StatRow
                      label="Highest"
                      value={fmtPrice(data.self_benchmark?.max_price)}
                    />
                    <PriceStatusNote
                      status={data.self_benchmark?.price_status}
                      diff={data.self_benchmark?.price_diff}
                      referenceLabel="your own average"
                    />
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: "#a1a1aa",
                        textTransform: "uppercase",
                        letterSpacing: ".04em",
                        marginBottom: 12,
                      }}
                    >
                      Position vs similar items
                    </div>
                    <PriceRangeBar
                      min={data.self_benchmark?.min_price}
                      max={data.self_benchmark?.max_price}
                      avg={data.self_benchmark?.avg_price}
                      our={data.our_listing?.price_parsed}
                      accent={accent}
                    />
                  </div>
                </div>
              </Card>
            )}

            <Card title="Title">
              <div
                style={{
                  background: "#fafafa",
                  borderRadius: 10,
                  padding: 14,
                  marginBottom: isEbay ? 16 : 0,
                }}
              >
                <div
                  style={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    color: "#a1a1aa",
                    textTransform: "uppercase",
                    letterSpacing: ".04em",
                    marginBottom: 6,
                  }}
                >
                  {isEbay
                    ? data.title_analysis?.our_title_length
                    : (data.our_listing?.title ?? "").length}{" "}
                  characters
                </div>
                <div
                  style={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.5 }}
                >
                  {isEbay
                    ? data.title_analysis?.our_title
                    : data.our_listing?.title}
                </div>
                {isEbay
                  ? data.title_analysis?.avg_comp_title_len && (
                      <div
                        style={{
                          fontSize: 11.5,
                          color: "#71717a",
                          marginTop: 8,
                        }}
                      >
                        Competitor average:{" "}
                        {data.title_analysis.avg_comp_title_len} chars
                        {data.title_analysis.our_title_length <
                        data.title_analysis.avg_comp_title_len
                          ? " — yours is shorter than most"
                          : " — length looks fine"}
                      </div>
                    )
                  : data.self_benchmark?.avg_title_length && (
                      <div
                        style={{
                          fontSize: 11.5,
                          color: "#71717a",
                          marginTop: 8,
                        }}
                      >
                        Your average for similar items:{" "}
                        {data.self_benchmark.avg_title_length} chars
                        {(data.our_listing?.title ?? "").length <
                        data.self_benchmark.avg_title_length * 0.7
                          ? " — this one is notably shorter"
                          : " — length looks fine"}
                      </div>
                    )}
              </div>

              {isEbay && data.title_analysis?.missing_keywords?.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#a1a1aa",
                      textTransform: "uppercase",
                      letterSpacing: ".04em",
                      marginBottom: 8,
                    }}
                  >
                    Missing from your title
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {data.title_analysis.missing_keywords.map((k) => (
                      <Tag key={k.word} tone="danger">
                        {k.word} · {k.pct}%
                      </Tag>
                    ))}
                  </div>
                </div>
              )}

              {isEbay && data.title_analysis?.unique_words?.length > 0 && (
                <div style={{ marginTop: 14 }}>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#a1a1aa",
                      textTransform: "uppercase",
                      letterSpacing: ".04em",
                      marginBottom: 8,
                    }}
                  >
                    Words only you use
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {data.title_analysis.unique_words.map((w) => (
                      <Tag key={w} tone="neutral">
                        {w}
                      </Tag>
                    ))}
                  </div>
                </div>
              )}
            </Card>

            {isEbay && (
              <Card title="Condition">
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 16,
                    marginBottom: 16,
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 10.5,
                        color: "#a1a1aa",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        marginBottom: 4,
                      }}
                    >
                      Yours
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 700 }}>
                      {data.condition_analysis.our_condition || "Not set"}
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: 10.5,
                        color: "#a1a1aa",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        marginBottom: 4,
                      }}
                    >
                      Most common (competitors)
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 700 }}>
                      {data.condition_analysis.dominant_condition || "—"}
                    </div>
                  </div>
                </div>
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 8 }}
                >
                  {Object.entries(
                    data.condition_analysis.condition_counts || {},
                  )
                    .sort((a, b) => b[1] - a[1])
                    .map(([cond, count]) => {
                      const total = Object.values(
                        data.condition_analysis.condition_counts,
                      ).reduce((a, b) => a + b, 0);
                      return (
                        <div
                          key={cond}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                          }}
                        >
                          <div
                            style={{
                              width: 150,
                              fontSize: 11.5,
                              color: "#3f3f46",
                              flexShrink: 0,
                            }}
                          >
                            {cond}
                          </div>
                          <div
                            style={{
                              flex: 1,
                              background: "#f0f0f1",
                              borderRadius: 99,
                              height: 7,
                              overflow: "hidden",
                            }}
                          >
                            <div
                              style={{
                                height: "100%",
                                borderRadius: 99,
                                background: accent,
                                width: `${(count / total) * 100}%`,
                              }}
                            />
                          </div>
                          <div
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: "#71717a",
                              width: 56,
                              textAlign: "right",
                            }}
                          >
                            {count} ({Math.round((count / total) * 100)}%)
                          </div>
                        </div>
                      );
                    })}
                </div>
              </Card>
            )}

            {isEbay ? (
              <Card title={`Top competitors (${data.competitors.total})`}>
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 8 }}
                >
                  {data.competitors.data.map((c, i) => {
                    const diff =
                      data.price_analysis.our_price && c.price_amount
                        ? (
                            data.price_analysis.our_price - c.price_amount
                          ).toFixed(2)
                        : null;
                    return (
                      <a
                        key={c.id}
                        href={c.external_url}
                        target="_blank"
                        rel="noreferrer"
                        className="la-list-row linked"
                      >
                        <RankBadge i={i} accent={accent} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            className="la-list-title"
                            style={{ color: "#18181b" }}
                          >
                            {c.title || "—"}
                          </div>
                          <div className="la-list-meta">
                            <span style={{ fontWeight: 700, color: "#18181b" }}>
                              {fmtPrice(c.price_amount, c.price_currency)}
                            </span>
                            <DiffTag diff={diff} />
                            {c.condition && (
                              <Tag tone="neutral">{c.condition}</Tag>
                            )}
                            {c.watcher_count > 0 && (
                              <span>{c.watcher_count} watching</span>
                            )}
                            {c.seller_id && <span>@{c.seller_id}</span>}
                          </div>
                        </div>
                      </a>
                    );
                  })}
                </div>
              </Card>
            ) : (
              <Card
                title={`Similar items in your catalog (${data.peers?.length ?? 0})`}
              >
                {!data.peers || data.peers.length === 0 ? (
                  <EmptyState
                    title="No matches yet"
                    sub="No other active listings share this brand and condition."
                  />
                ) : (
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 8 }}
                  >
                    {data.peers.map((p, i) => {
                      const pPrice =
                        p.price != null
                          ? Number(String(p.price).replace(/[^\d.]/g, ""))
                          : null;
                      const diff =
                        data.our_listing?.price_parsed != null && pPrice != null
                          ? (data.our_listing.price_parsed - pPrice).toFixed(2)
                          : null;
                      return (
                        <div key={p.product_id} className="la-list-row">
                          <RankBadge i={i} accent={accent} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="la-list-title">
                              {p.title || "—"}
                            </div>
                            <div className="la-list-meta">
                              <span
                                style={{ fontWeight: 700, color: "#18181b" }}
                              >
                                {fmtPrice(pPrice)}
                              </span>
                              <DiffTag diff={diff} />
                              <span>{fmt(p.views)} views</span>
                              <span>{fmt(p.favourites)} favourites</span>
                              {p.size && <Tag tone="neutral">{p.size}</Tag>}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const HeaderStat = ({ label, value, big, mono }) => (
  <div>
    <div
      style={{
        fontSize: 11,
        color: "#a1a1aa",
        fontWeight: 600,
        marginBottom: 3,
      }}
    >
      {label}
    </div>
    <div
      style={{
        fontSize: big ? 19 : 13.5,
        fontWeight: big ? 700 : 600,
        color: big ? "#18181b" : "#3f3f46",
        fontFamily: mono ? "ui-monospace,monospace" : "inherit",
      }}
    >
      {value}
    </div>
  </div>
);

const FunnelStep = ({ label, value, sub, accent }) => (
  <div
    style={{
      background: "#fafafa",
      border: "1px solid #f0f0f1",
      borderRadius: 10,
      padding: "14px",
      textAlign: "center",
    }}
  >
    <div style={{ fontSize: 21, fontWeight: 700, color: accent }}>{value}</div>
    <div
      style={{ fontSize: 11, fontWeight: 700, color: "#3f3f46", marginTop: 3 }}
    >
      {label}
    </div>
    <div style={{ fontSize: 10.5, color: "#a1a1aa", marginTop: 2 }}>{sub}</div>
  </div>
);

const MiniStat = ({ label, value, danger, warn }) => (
  <div style={{ background: "#fafafa", borderRadius: 8, padding: "12px 14px" }}>
    <div
      style={{
        fontSize: 10.5,
        color: "#a1a1aa",
        fontWeight: 700,
        textTransform: "uppercase",
        marginBottom: 4,
      }}
    >
      {label}
    </div>
    <div
      style={{
        fontSize: 18,
        fontWeight: 700,
        color: danger ? "#b91c1c" : warn ? "#92400e" : "#18181b",
      }}
    >
      {value}
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// QUICK VIEW DRAWER
// ─────────────────────────────────────────────────────────────────────────────
const QuickViewDrawer = ({ marketplace, itemId, onClose, onViewFull }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isEbay = marketplace === "ebay";
  const accent = MARKETPLACE_CFG[marketplace].accent;

  useEffect(() => {
    setLoading(true);
    setError(null);
    setData(null);
    const url = isEbay
      ? `${API_BASE}/analysis/${itemId}`
      : `${API_BASE}/vinted/analysis/${itemId}`;
    axios
      .get(url)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.message || e.message))
      .finally(() => setLoading(false));
  }, [itemId, isEbay]);

  return (
    <div className="la-drawer" style={{ animation: "la-slide-in .2s ease" }}>
      <div
        style={{
          padding: "16px 20px",
          borderBottom: "1px solid #f0f0f1",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <div>
          <div style={{ fontSize: 13.5, fontWeight: 700 }}>Quick view</div>
          <div className="la-row-id">{itemId}</div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {data && (
            <button
              className="la-btn la-btn-primary la-btn-sm"
              onClick={onViewFull}
            >
              Full analysis
            </button>
          )}
          <button className="la-btn la-btn-ghost la-btn-icon" onClick={onClose}>
            ✕
          </button>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: 20 }}>
        {loading && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <Sk w="70%" h={15} />
            <Sk w="100%" h={70} />
            {[...Array(3)].map((_, i) => (
              <Sk key={i} w="100%" h={50} />
            ))}
          </div>
        )}

        {error && (
          <div
            style={{
              background: "#fef2f2",
              borderRadius: 10,
              padding: 14,
              color: "#991b1b",
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        {data && !loading && (
          <>
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                marginBottom: 12,
                lineHeight: 1.4,
              }}
            >
              {data.our_listing?.title}
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4,1fr)",
                gap: 8,
                marginBottom: 20,
              }}
            >
              {(isEbay
                ? [
                    ["Price", fmtPrice(data.our_listing?.price)],
                    ["Impressions", fmt(data.our_listing?.impressions)],
                    ["CTR", pct(data.our_listing?.ctr)],
                    ["Sold", fmt(data.our_listing?.sold)],
                  ]
                : [
                    ["Price", fmtPrice(data.our_listing?.price_parsed)],
                    ["Views", fmt(data.our_listing?.views)],
                    ["Favourites", fmt(data.our_listing?.favourites)],
                    ["Days listed", fmt(data.our_listing?.days_since_upload)],
                  ]
              ).map(([k, v]) => (
                <div
                  key={k}
                  style={{
                    background: "#fafafa",
                    borderRadius: 8,
                    padding: "8px 6px",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: 9.5,
                      color: "#a1a1aa",
                      fontWeight: 700,
                      textTransform: "uppercase",
                    }}
                  >
                    {k}
                  </div>
                  <div
                    style={{ fontSize: 13.5, fontWeight: 700, marginTop: 3 }}
                  >
                    {v}
                  </div>
                </div>
              ))}
            </div>

            {data.actions?.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <SectionLabel>
                  {data.actions.length} issue
                  {data.actions.length === 1 ? "" : "s"} to fix
                </SectionLabel>
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 8 }}
                >
                  {data.actions.slice(0, 3).map((a, i) => {
                    const cfg = PRIORITY_CFG[a.priority] || PRIORITY_CFG.low;
                    return (
                      <div
                        key={i}
                        style={{
                          borderLeft: `3px solid ${cfg.color}`,
                          background: "#fafafa",
                          borderRadius: 8,
                          padding: "10px 12px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 11.5,
                            fontWeight: 700,
                            marginBottom: 3,
                          }}
                        >
                          {a.issue}
                        </div>
                        <div style={{ fontSize: 11.5, color: "#71717a" }}>
                          {a.fix}
                        </div>
                      </div>
                    );
                  })}
                  {data.actions.length > 3 && (
                    <button
                      className="la-btn la-btn-ghost la-btn-sm"
                      style={{ justifyContent: "center" }}
                      onClick={onViewFull}
                    >
                      +{data.actions.length - 3} more → Full analysis
                    </button>
                  )}
                </div>
              </div>
            )}

            <div style={{ marginBottom: 20 }}>
              <SectionLabel>
                {isEbay ? "Price vs competitors" : "Price vs your own catalog"}
              </SectionLabel>
              <div
                style={{ background: "#fafafa", borderRadius: 10, padding: 14 }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3,1fr)",
                    gap: 8,
                    marginBottom: 12,
                  }}
                >
                  {(isEbay
                    ? [
                        ["Yours", fmtPrice(data.price_analysis.our_price)],
                        ["Average", fmtPrice(data.price_analysis.avg_price)],
                        ["Lowest", fmtPrice(data.price_analysis.min_price)],
                      ]
                    : [
                        ["Yours", fmtPrice(data.our_listing?.price_parsed)],
                        ["Average", fmtPrice(data.self_benchmark?.avg_price)],
                        ["Lowest", fmtPrice(data.self_benchmark?.min_price)],
                      ]
                  ).map(([k, v]) => (
                    <div key={k} style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: 9.5,
                          color: "#a1a1aa",
                          fontWeight: 700,
                          textTransform: "uppercase",
                        }}
                      >
                        {k}
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 700 }}>{v}</div>
                    </div>
                  ))}
                </div>
                {isEbay ? (
                  <PriceBandChart
                    bands={data.price_analysis.price_bands}
                    ourPrice={data.price_analysis.our_price}
                    accent={accent}
                  />
                ) : (
                  <PriceRangeBar
                    min={data.self_benchmark?.min_price}
                    max={data.self_benchmark?.max_price}
                    avg={data.self_benchmark?.avg_price}
                    our={data.our_listing?.price_parsed}
                    accent={accent}
                  />
                )}
              </div>
            </div>

            {isEbay && data.title_analysis.missing_keywords?.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <SectionLabel>Missing from your title</SectionLabel>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {data.title_analysis.missing_keywords.slice(0, 5).map((k) => (
                    <Tag key={k.word} tone="danger">
                      {k.word} · {k.pct}%
                    </Tag>
                  ))}
                </div>
              </div>
            )}

            <div>
              <SectionLabel>
                {isEbay
                  ? `Top competitors (${data.competitors.total})`
                  : `Similar items (${data.peers?.length ?? 0})`}
              </SectionLabel>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {(isEbay ? data.competitors.data : (data.peers ?? []))
                  .slice(0, 5)
                  .map((c, i) => {
                    const cPrice = isEbay
                      ? c.price_amount
                      : c.price != null
                        ? Number(String(c.price).replace(/[^\d.]/g, ""))
                        : null;
                    const ourP = isEbay
                      ? data.price_analysis.our_price
                      : data.our_listing?.price_parsed;
                    const diff =
                      ourP != null && cPrice != null
                        ? (ourP - cPrice).toFixed(2)
                        : null;
                    const key = isEbay ? c.id : c.product_id;
                    const inner = (
                      <>
                        <RankBadge i={i} accent={accent} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            className="la-list-title"
                            style={{ color: "#3f3f46" }}
                          >
                            {c.title || "—"}
                          </div>
                          <div className="la-list-meta">
                            <span style={{ fontWeight: 700, color: "#18181b" }}>
                              {fmtPrice(cPrice)}
                            </span>
                            <DiffTag diff={diff} />
                            {isEbay && c.condition && (
                              <Tag tone="neutral">{c.condition}</Tag>
                            )}
                            {!isEbay && (
                              <span>
                                {fmt(c.views)} views · {fmt(c.favourites)} favs
                              </span>
                            )}
                          </div>
                        </div>
                      </>
                    );
                    return isEbay ? (
                      <a
                        key={key}
                        href={c.external_url}
                        target="_blank"
                        rel="noreferrer"
                        className="la-list-row linked"
                      >
                        {inner}
                      </a>
                    ) : (
                      <div key={key} className="la-list-row">
                        {inner}
                      </div>
                    );
                  })}
                {isEbay && data.competitors.total > 5 && (
                  <button
                    className="la-btn la-btn-ghost la-btn-sm"
                    style={{ justifyContent: "center" }}
                    onClick={onViewFull}
                  >
                    +{data.competitors.total - 5} more → Full analysis
                  </button>
                )}
                {!isEbay && (data.peers?.length ?? 0) > 5 && (
                  <button
                    className="la-btn la-btn-ghost la-btn-sm"
                    style={{ justifyContent: "center" }}
                    onClick={onViewFull}
                  >
                    +{data.peers.length - 5} more → Full analysis
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const SectionLabel = ({ children }) => (
  <div
    style={{
      fontSize: 10.5,
      fontWeight: 700,
      color: "#a1a1aa",
      textTransform: "uppercase",
      letterSpacing: ".05em",
      marginBottom: 10,
    }}
  >
    {children}
  </div>
);

const SearchIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

// ─────────────────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────────────────
const ListingAgentPage = () => {
  const [marketplace, setMarketplace] = useState("ebay");
  const [view, setView] = useState("list");
  const [detailId, setDetailId] = useState(null);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [listings, setListings] = useState([]);
  const [totalMatching, setTotalMatching] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const PAGE_SIZE = 100;
  const [findingCompetitors, setFindingCompetitors] = useState(false);
  const [findProgress, setFindProgress] = useState(null); // { processed, total, errorCount }
  const [findingOne, setFindingOne] = useState(null);
  const [drawerItem, setDrawerItem] = useState(null);
  const [toast, setToast] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [profileUsername, setProfileUsername] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [onlyLowPerforming, setOnlyLowPerforming] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const pollRef = useRef(null);

  const debouncedSearch = useDebounced(searchInput, 350);

  const isEbay = marketplace === "ebay";
  // eslint-disable-next-line
  const accent = MARKETPLACE_CFG[marketplace].accent;

  useEffect(() => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      setCompanyId(user.company_id || user.companyId || null);
      setProfileUsername(user.vinted_username || user.profile_username || null);
    } catch {
      setCompanyId(null);
      setProfileUsername(null);
    }
  }, []);

  useEffect(
    () => () => {
      if (pollRef.current) clearInterval(pollRef.current);
    },
    [],
  );

  const showToast = (msg, type = "ok") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const url = isEbay ? `${API_BASE}/stats` : `${API_BASE}/vinted/stats`;
      const params = isEbay
        ? { company_id: companyId }
        : profileUsername
          ? { profile_username: profileUsername }
          : {};
      const r = await axios.get(url, { params });
      if (r.data.success) setStats(r.data);
    } catch (e) {
      console.error(e.message);
    } finally {
      setStatsLoading(false);
    }
  }, [isEbay, companyId, profileUsername]);

  // Uses the new /all (or /vinted/all) endpoints — DB-paginated, so this
  // fetches one page (PAGE_SIZE rows) at a time. `reset=true` replaces the
  // list (new search/filter/marketplace); `reset=false` appends the next
  // page onto what's already loaded ("Load more").
  const fetchListings = useCallback(
    async (reset) => {
      const offset = reset ? 0 : listings.length;
      reset ? setLoading(true) : setLoadingMore(true);
      try {
        const url = isEbay ? `${API_BASE}/all` : `${API_BASE}/vinted/all`;
        const params = {
          limit: PAGE_SIZE,
          offset,
          only_low_performing: onlyLowPerforming ? "true" : "false",
        };
        if (debouncedSearch) params.q = debouncedSearch;
        if (isEbay && companyId) params.company_id = companyId;
        if (!isEbay && profileUsername)
          params.profile_username = profileUsername;
        const r = await axios.get(url, { params });
        if (r.data.success) {
          setListings((prev) =>
            reset ? r.data.data || [] : [...prev, ...(r.data.data || [])],
          );
          setTotalMatching(r.data.total ?? 0);
          setHasMore(!!r.data.has_more);
        }
      } catch (e) {
        console.error(e.message);
      } finally {
        reset ? setLoading(false) : setLoadingMore(false);
      }
    },
    [
      isEbay,
      companyId,
      profileUsername,
      onlyLowPerforming,
      debouncedSearch,
      listings.length,
    ],
  );

  useEffect(() => {
    fetchStats();
    setView("list");
    setDetailId(null);
    setDrawerItem(null);
    setSearchInput("");
  }, [marketplace]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset + refetch page 1 whenever marketplace, search text, or the
  // low-performing toggle changes. (Not depending on fetchListings itself,
  // since that identity changes with listings.length — we only want this to
  // fire on the filters, and "Load more" calls fetchListings(false) directly.)
  useEffect(() => {
    fetchListings(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEbay, companyId, profileUsername, onlyLowPerforming, debouncedSearch]);

  // "Find all competitors" now kicks off a background job on the server and
  // returns immediately (see backend notes) — this polls /find-competitors/status
  // every 2s so long runs (50+ listings) don't rely on one long-lived HTTP
  // request surviving proxy/host timeouts.
  const pollFindStatus = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API_BASE}/find-competitors/status`, {
          params: companyId ? { company_id: companyId } : {},
        });
        if (r.data.success) {
          setFindProgress({
            processed: r.data.processed,
            total: r.data.total,
            errorCount: r.data.errorCount,
          });
          if (!r.data.running) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setFindingCompetitors(false);
            showToast(
              `Done — processed ${r.data.processed}/${r.data.total} listings${r.data.errorCount ? ` (${r.data.errorCount} errors)` : ""}`,
            );
            fetchStats();
            fetchListings(true);
          }
        }
      } catch (e) {
        console.error(e.message);
      }
    }, 2000);
  }, [companyId, fetchStats, fetchListings]);

  const handleFindAll = async () => {
    setFindingCompetitors(true);
    setFindProgress(null);
    try {
      const body = {};
      if (companyId) body.company_id = companyId;
      const r = await axios.post(`${API_BASE}/find-competitors`, body);
      if (!r.data.started) {
        showToast(r.data.message || "Nothing to process");
        setFindingCompetitors(false);
        return;
      }
      setFindProgress({ processed: 0, total: r.data.total, errorCount: 0 });
      pollFindStatus();
    } catch (e) {
      showToast(e.response?.data?.error || e.message, "err");
      setFindingCompetitors(false);
    }
  };

  const handleFindOne = async (id) => {
    setFindingOne(id);
    try {
      const body = { ebay_item_ids: [id] };
      if (companyId) body.company_id = companyId;
      const r = await axios.post(`${API_BASE}/find-competitors`, body);
      // Single-item runs are quick, but they now go through the same
      // background job — poll briefly until it's done.
      if (r.data.started) {
        const poll = setInterval(async () => {
          const s = await axios.get(`${API_BASE}/find-competitors/status`, {
            params: companyId ? { company_id: companyId } : {},
          });
          if (!s.data.running) {
            clearInterval(poll);
            setFindingOne(null);
            showToast("Competitors updated");
            fetchStats();
          }
        }, 1500);
      } else {
        setFindingOne(null);
      }
    } catch (e) {
      showToast(e.response?.data?.error || e.message, "err");
      setFindingOne(null);
    }
  };

  const itemKey = (item) => (isEbay ? item.ebay_item_id : item.product_id);

  if (view === "detail" && detailId) {
    return (
      <ListingDetailPage
        marketplace={marketplace}
        itemId={detailId}
        onBack={() => {
          setView("list");
          setDetailId(null);
        }}
      />
    );
  }

  const totalRecs =
    isEbay && stats
      ? (stats.recommendations || []).reduce((s, r) => s + Number(r.count), 0)
      : 0;
  const pendingRecs =
    isEbay && stats
      ? ((stats.recommendations || []).find((r) => r.status === "pending")
          ?.count ?? 0)
      : 0;

  const statCards = isEbay
    ? [
        {
          label: "Matching this view",
          val: fmt(totalMatching),
          sub:
            onlyLowPerforming || debouncedSearch
              ? "with current filters"
              : "all listings",
        },
        {
          label: "Low performing",
          val: fmt(stats?.low_performing_listings ?? 0),
          sub: `impressions < ${stats?.rules?.impressions_below ?? 20} or CTR < ${stats?.rules?.ctr_below ?? 2}%`,
        },
        {
          label: "Competitors tracked",
          val: fmt(stats?.competitors?.total ?? 0),
          sub: `${fmt(stats?.competitors?.sold ?? 0)} sold or ended`,
        },
        {
          label: "Recommendations",
          val: fmt(totalRecs),
          sub: `${fmt(pendingRecs)} pending review`,
        },
      ]
    : [
        {
          label: "Active listings",
          val: fmt(stats?.total_active_listings ?? 0),
          sub: "currently live on Vinted",
        },
        {
          label: "Matching this view",
          val: fmt(totalMatching),
          sub:
            onlyLowPerforming || debouncedSearch
              ? "with current filters"
              : "all listings",
        },
        {
          label: "Low performing",
          val: fmt(stats?.low_performing_listings ?? 0),
          sub: `views < ${stats?.rules?.views_below ?? 10} or favourites < ${stats?.rules?.favourites_below ?? 1}`,
        },
      ];

  const tableHeaders = isEbay
    ? [
        "Title",
        "SKU",
        "Impressions",
        "Views",
        "CTR",
        "Sold",
        "Price",
        "Status",
        "",
      ]
    : [
        "Title",
        "SKU",
        "Views",
        "Favourites",
        "Views / day",
        "Age (days)",
        "Price",
        "Status",
        "",
      ];

  return (
    <div className="la-root">
      <style>{GLOBAL_STYLES}</style>

      {toast && (
        <div
          className="la-toast"
          style={{
            background: toast.type === "ok" ? "#f4faf6" : "#fef2f2",
            border: `1px solid ${toast.type === "ok" ? "#cfe6d6" : "#fecaca"}`,
            color: toast.type === "ok" ? "#276340" : "#991b1b",
          }}
        >
          {toast.msg}
        </div>
      )}

      {drawerItem && (
        <>
          <div
            className="la-drawer-overlay"
            onClick={() => setDrawerItem(null)}
          />
          <QuickViewDrawer
            marketplace={marketplace}
            itemId={drawerItem}
            onClose={() => setDrawerItem(null)}
            onViewFull={() => {
              setDrawerItem(null);
              setDetailId(drawerItem);
              setView("detail");
            }}
          />
        </>
      )}

      {rulesOpen && (
        <RulesModal
          marketplace={marketplace}
          scope={
            isEbay
              ? { company_id: companyId }
              : { profile_username: profileUsername }
          }
          onClose={() => setRulesOpen(false)}
          onSaved={(saved) => {
            setRulesOpen(false);
            showToast("Rules saved");
            fetchStats();
            fetchListings(true);
          }}
        />
      )}

      <div className="la-shell">
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 16,
            marginBottom: 20,
          }}
        >
          <div>
            <div className="la-eyebrow">Listing intelligence</div>
            <h1 className="la-title">Listing agent</h1>
            <p className="la-subtitle">
              {isEbay
                ? "Every listing, benchmarked against eBay competitors, with what to fix explained."
                : "Every listing, benchmarked against your own catalog, with what to fix explained."}
            </p>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              className="la-btn la-btn-ghost"
              onClick={() => setRulesOpen(true)}
            >
              Edit rules
            </button>
            {isEbay && (
              <button
                className="la-btn la-btn-primary"
                disabled={findingCompetitors || loading}
                onClick={handleFindAll}
              >
                {findingCompetitors ? (
                  <>
                    <span
                      className="la-spin"
                      style={{ width: 13, height: 13 }}
                    />{" "}
                    Finding…
                  </>
                ) : (
                  "Find all competitors"
                )}
              </button>
            )}
          </div>
        </div>

        {/* Marketplace switch */}
        <div style={{ marginBottom: 24 }}>
          <div className="la-segment">
            {Object.entries(MARKETPLACE_CFG).map(([key, cfg]) => (
              <button
                key={key}
                className={marketplace === key ? "active" : ""}
                onClick={() => setMarketplace(key)}
              >
                {cfg.label}
              </button>
            ))}
          </div>
        </div>

        {isEbay && findingCompetitors && (
          <div className="la-card" style={{ marginBottom: 20 }}>
            <div className="la-card-body">
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>
                {findProgress
                  ? `Searching eBay for competitors — ${findProgress.processed}/${findProgress.total}${findProgress.errorCount ? ` (${findProgress.errorCount} errors)` : ""}`
                  : "Starting…"}
              </div>
              <div className="la-progress-track">
                <div
                  className="la-progress-fill"
                  style={{
                    width: findProgress?.total
                      ? `${Math.min(100, Math.round((findProgress.processed / findProgress.total) * 100))}%`
                      : "8%",
                    background: "#3f3f46",
                  }}
                />
              </div>
              <div style={{ fontSize: 11, color: "#a1a1aa", marginTop: 8 }}>
                Runs in the background — you can keep browsing while this
                finishes.
              </div>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="la-stats">
          {statCards.map((s) => (
            <div key={s.label} className="la-stat">
              <div className="la-stat-label">{s.label}</div>
              {statsLoading ? (
                <Sk w={56} h={28} />
              ) : (
                <div className="la-stat-value" style={{ color: "#18181b" }}>
                  {s.val}
                </div>
              )}
              <div className="la-stat-sub">{s.sub}</div>
            </div>
          ))}
        </div>

        {/* Table */}
        <div className="la-card">
          <div className="la-card-head">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                flexWrap: "wrap",
              }}
            >
              <span className="la-card-title">Listings</span>
              {!loading && <Tag tone="neutral">{listings.length}</Tag>}
              <div className="la-search">
                <SearchIcon />
                <input
                  type="text"
                  placeholder="Search by title or SKU…"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </div>
              <label className="la-toggle-row">
                <input
                  type="checkbox"
                  checked={onlyLowPerforming}
                  onChange={(e) => setOnlyLowPerforming(e.target.checked)}
                />
                Only low performing
              </label>
            </div>
            <button
              className="la-btn la-btn-ghost la-btn-sm"
              onClick={() => fetchListings(true)}
              disabled={loading}
            >
              {loading ? "Loading…" : "Refresh"}
            </button>
          </div>

          {!loading && listings.length === 0 && (
            <EmptyState
              title={debouncedSearch ? "No matches" : "No listings found"}
              sub={
                debouncedSearch
                  ? "Try a different search term."
                  : "Nothing to show yet."
              }
            />
          )}

          {(loading || listings.length > 0) && (
            <div className="la-table-wrap">
              <table className="la-table">
                <thead>
                  <tr>
                    {tableHeaders.map((h) => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading
                    ? [...Array(8)].map((_, i) => (
                        <tr key={i}>
                          <td>
                            <Sk w={220} h={13} style={{ marginBottom: 6 }} />
                            <Sk w={70} h={10} />
                          </td>
                          {Array.from({ length: tableHeaders.length - 1 }).map(
                            (__, j) => (
                              <td key={j}>
                                <Sk w={50 + (j % 3) * 20} h={15} />
                              </td>
                            ),
                          )}
                        </tr>
                      ))
                    : listings.map((item, i) => {
                        const id = itemKey(item);
                        const isFindingThis = findingOne === id;
                        const sku = isEbay
                          ? item.sku
                          : item.internal_product_id || item.product_id;

                        return (
                          <tr
                            key={id || i}
                            style={{
                              animation: `la-fade-up .2s ease ${i * 0.015}s both`,
                            }}
                          >
                            <td style={{ maxWidth: 280 }}>
                              {isEbay ? (
                                <a
                                  href={item.item_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="la-row-title linked"
                                  style={{ color: "#18181b" }}
                                  title={item.title}
                                >
                                  {truncate(item.title, 52)}
                                </a>
                              ) : (
                                <span
                                  className="la-row-title"
                                  title={item.title}
                                >
                                  {truncate(item.title, 52)}
                                </span>
                              )}
                              <span className="la-row-id">{id}</span>
                            </td>

                            <td>
                              <span className="la-row-sku">{sku || "—"}</span>
                            </td>

                            {isEbay ? (
                              <>
                                <td>
                                  <span
                                    style={{
                                      fontWeight: 600,
                                      color: "#18181b",
                                    }}
                                  >
                                    {fmt(item.impressions)}
                                  </span>
                                </td>
                                <td>
                                  <span
                                    style={{
                                      fontWeight: 600,
                                      color: "#3f3f46",
                                    }}
                                  >
                                    {fmt(item.ebay_views)}
                                  </span>
                                </td>
                                <td>
                                  <span
                                    style={{
                                      fontWeight: 600,
                                      color: "#3f3f46",
                                    }}
                                  >
                                    {item.ctr ?? item.click_through_rate ?? "—"}
                                    {(item.ctr ?? item.click_through_rate) !=
                                    null
                                      ? "%"
                                      : ""}
                                  </span>
                                </td>
                                <td>
                                  <span
                                    style={{
                                      fontWeight: 600,
                                      color: "#3f3f46",
                                    }}
                                  >
                                    {fmt(item.quantity_sold)}
                                  </span>
                                </td>
                                <td>
                                  <span style={{ fontWeight: 600 }}>
                                    {fmtPrice(item.price, item.currency)}
                                  </span>
                                </td>
                              </>
                            ) : (
                              <>
                                <td>
                                  <span
                                    style={{
                                      fontWeight: 600,
                                      color: "#18181b",
                                    }}
                                  >
                                    {fmt(item.views)}
                                  </span>
                                </td>
                                <td>
                                  <span
                                    style={{
                                      fontWeight: 600,
                                      color: "#3f3f46",
                                    }}
                                  >
                                    {fmt(item.favourites)}
                                  </span>
                                </td>
                                <td>
                                  <span
                                    style={{
                                      fontWeight: 600,
                                      color: "#3f3f46",
                                    }}
                                  >
                                    {item.views_per_day ?? "—"}
                                  </span>
                                </td>
                                <td>
                                  <span
                                    style={{
                                      fontWeight: 600,
                                      color: "#3f3f46",
                                    }}
                                  >
                                    {item.days_since_upload ?? "—"}
                                  </span>
                                </td>
                                <td>
                                  <span style={{ fontWeight: 600 }}>
                                    {fmtPrice(item.price_parsed)}
                                  </span>
                                </td>
                              </>
                            )}

                            <td>
                              <IssueBadge issues={item.issues} />
                            </td>

                            <td>
                              <div
                                style={{
                                  display: "flex",
                                  gap: 6,
                                  justifyContent: "flex-end",
                                }}
                              >
                                <button
                                  className="la-btn la-btn-ghost la-btn-sm"
                                  onClick={() => setDrawerItem(id)}
                                >
                                  Quick view
                                </button>
                                <button
                                  className="la-btn la-btn-ghost la-btn-sm"
                                  onClick={() => {
                                    setDetailId(id);
                                    setView("detail");
                                  }}
                                >
                                  Full analysis
                                </button>
                                {isEbay && (
                                  <button
                                    className="la-btn la-btn-ghost la-btn-icon"
                                    disabled={isFindingThis}
                                    onClick={() => handleFindOne(id)}
                                    title="Re-find competitors"
                                  >
                                    {isFindingThis ? (
                                      <span
                                        className="la-spin"
                                        style={{
                                          width: 11,
                                          height: 11,
                                          borderTopColor: "#71717a",
                                          borderColor: "rgba(0,0,0,.12)",
                                        }}
                                      />
                                    ) : (
                                      "↻"
                                    )}
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                </tbody>
              </table>
            </div>
          )}

          {!loading && listings.length > 0 && (
            <div
              style={{
                padding: "16px 20px",
                borderTop: "1px solid #f0f0f1",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ fontSize: 12, color: "#a1a1aa" }}>
                Showing {fmt(listings.length)} of {fmt(totalMatching)}
              </span>
              {hasMore && (
                <button
                  className="la-btn la-btn-ghost la-btn-sm"
                  onClick={() => fetchListings(false)}
                  disabled={loadingMore}
                >
                  {loadingMore ? (
                    <>
                      <span
                        className="la-spin"
                        style={{
                          width: 11,
                          height: 11,
                          borderTopColor: "#71717a",
                          borderColor: "rgba(0,0,0,.12)",
                        }}
                      />{" "}
                      Loading…
                    </>
                  ) : (
                    `Load ${Math.min(PAGE_SIZE, totalMatching - listings.length)} more`
                  )}
                </button>
              )}
            </div>
          )}
        </div>

        <div
          style={{
            marginTop: 20,
            fontSize: 11,
            color: "#d4d4d8",
            textAlign: "center",
          }}
        >
          {isEbay
            ? "eBay Seller Hub data · Competitors via eBay Browse API"
            : "Vinted scraped data · Benchmarked against your own active catalog"}
        </div>
      </div>
    </div>
  );
};

export default ListingAgentPage;
